// Target: src/modules/project/components/ProjectDetailsView/components/ProjectBillingSectionTabContent/components/ProjectBillingSettingsTabContent/components/UpdateProjectBillingSettingsSidebar/UpdateProjectBillingSettingsForm.tsx
import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";

import { setServerFieldErrors } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button/Button.tsx";
import { FormSectionGroup } from "~/components/formElements/FormSection";
import SubmitButton from "~/components/formElements/SubmitButton";
import Headline from "~/components/Headline/Headline";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import useClientsContactPersonsSelectOptions
	from "~/hooks/form/formOptionsData/useClientsContactPersonsSelectOptions.tsx";
import useClientsInvoiceRecipientSelectOptions
	from "~/hooks/form/formOptionsData/useClientsInvoiceRecipientsSelectOptions.ts";
import useCurrencySelectOptions from "~/hooks/form/formOptionsData/useCurrencySelectOptions.ts";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import type {
	BillingSettings,
	BillingSettingsInvoiceTypeId,
} from "~/modules/billing/api/billingSettings/billingSettingsTypes.ts";
import BillingSettingsFormSections from "~/modules/billing/components/formSections/BillingSettingsFormSections.tsx";
import { useBillingSettingsLookups } from "~/modules/billing/hooks/useBillingSettingsLookups.ts";
import { computeBillingSettingsDifferences } from "~/modules/billing/utils/billingSettingsDisplayUtils.ts";
import type { BillingSettingsBaseFormData } from "~/modules/billing/utils/billingSettingsFormUtils.ts";
import {
	createBillingSettingsBaseSchema,
	getBillingSettingsBaseDefaultValues,
	prepareOrderBillingSettingsPayload,
	serializeAdditionalEmailRecipients,
} from "~/modules/billing/utils/billingSettingsFormUtils.ts";
import { updateOrderBillingSettings } from "~/modules/project/api/order/orderApiDispatchers.ts";
import type { Order } from "~/modules/project/api/order/orderTypes.ts";
import { normalizeTKey } from "~/types/typeHelpers.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type Props = {
	clientId: string;
	onCancel: () => void;
	onSuccess: () => void;
	order: Order
	orderBillingSettings: BillingSettings;
	projectBillingSettings: BillingSettings;
};

const UpdateOrderBillingSettingsForm: React.FC<Props> = ({
	clientId,
	onCancel,
	onSuccess,
	order,
	orderBillingSettings,
	projectBillingSettings,
}) => {
	const [busy, setBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState<string>("");
	const billingSettingsLookups = useBillingSettingsLookups(clientId);
	const schema = useMemo(() => createBillingSettingsBaseSchema(), []);
	const defaultValues = useMemo(
		() => getBillingSettingsBaseDefaultValues(orderBillingSettings),
		[orderBillingSettings],
	);
	console.log(orderBillingSettings);
	const {
		control,
		formState: { errors, isDirty, isSubmitted, isValid },
		handleSubmit,
		setError,
		setValue,
		trigger,
		watch,
	} = useForm<BillingSettingsBaseFormData>({
		defaultValues,
		mode: "onChange",
		resolver: yupResolver<BillingSettingsBaseFormData>(schema),
	});

	const clientContactPersonSelectOptions = useClientsContactPersonsSelectOptions(
		clientId,
		orderBillingSettings.clientContactPersonId ?? undefined,
	);
	const invoiceRecipientSelectOptions = useClientsInvoiceRecipientSelectOptions(clientId);
	const currencySelectOptions = useCurrencySelectOptions();

	const formIsSubmittable = useFormIsSubmittable({
		enableInitially: false,
		isDirty,
		isLoading: busy,
		isSubmitted,
		isValid,
	});

	const isForeignCountry = watch("isForeignCountry");
	const showCustomPaymentTargetInput = watch("paymentTargetDays") === 0;


	useEffect(() => {
		if (isSubmitted) {
			trigger("currencyId");
		}
	}, [isForeignCountry, isSubmitted, trigger]);

	useEffect(() => {
		if (showCustomPaymentTargetInput) {
			trigger("paymentTargetDaysCustom");
		}
	}, [showCustomPaymentTargetInput, trigger]);

	const onSubmit: SubmitHandler<BillingSettingsBaseFormData> = async (data) => {
		setBusy(true);
		try {
			const payload = prepareOrderBillingSettingsPayload(data);
			await updateOrderBillingSettings({
				projectId: order.projectId,
				orderId: order.id,
				billingSettingsData: payload,
			});
			onSuccess();
		} catch (error) {
			// The backend refuses a payload without exactly one recipient on the invoiceRecipientId field.
			if (!setServerFieldErrors(error, ["invoiceRecipientId"], setError)) {
				setServerErrorMessage(t("form:unexpectedError"));
			}
			setBusy(false);
		}
	};
	const formData = watch();

	const currentFormDataForDiffing = {
		...formData,
		clientContactPersonId: formData.clientContactPersonId || "",
		invoiceTypeId: formData.invoiceTypeId as BillingSettingsInvoiceTypeId,
		invoiceRecipientId: formData.invoiceRecipientId || "",
		additionalEmailRecipients: serializeAdditionalEmailRecipients(formData.additionalEmailRecipients),
	};

	const fieldsDivergingFromProject = computeBillingSettingsDifferences(projectBillingSettings, currentFormDataForDiffing, billingSettingsLookups, clientId);

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			onKeyDown={preventSubmitOnEnter}
			className="auto-grid-sidebar-double-header"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Abrechnungsdetails bearbeiten</SidebarHeaderHeadline>
			</SidebarHeader>
			<div className="bg-gray-200 px-7 py-2">
				<Headline type="h5">
					Bestellung: {order.title}
				</Headline>
			</div>
			<SidebarContent>
				{busy && <SidebarBusyOverlay />}
				{serverErrorMessage &&
					<SidebarErrorOverlay title="Update fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}
				<FormSectionGroup>
					<BillingSettingsFormSections
						clientContactPersonSelectOptions={clientContactPersonSelectOptions}
						control={control}
						currencySelectOptions={currencySelectOptions}
						errors={errors}
						fieldsDivergingFromProject={fieldsDivergingFromProject}
						invoiceRecipientSelectOptions={invoiceRecipientSelectOptions}
						isSubmitted={isSubmitted}
						setValue={setValue}
						showCombineOrders={false}
						trigger={trigger}
						watch={watch}
					/>
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<SubmitButton busy={busy}
							  disabled={!formIsSubmittable}>
					{t(normalizeTKey("form:buttonTexts.save"))}
				</SubmitButton>
				<Button theme="none"
						onClick={onCancel}>
					{t(normalizeTKey("form:buttonTexts.cancel"))}
				</Button>
			</SidebarFooter>
		</form>
	);
};

export default UpdateOrderBillingSettingsForm;